import type { StreamMessage } from "../../platform.ts";

/**
 * A server-sent events parser over a byte stream (the WHATWG event stream format). The
 * browser adapter reads the stream with `fetch` rather than `EventSource`, for two things
 * the contract needs that `EventSource` cannot do: set `Last-Event-ID` on a connection the
 * client reconstructs itself (§ Resume), and see the `: keepalive` comment lines the
 * 60 second dead-connection timer resets on (§ State machine), which `EventSource` swallows.
 */
export interface SseHandlers {
  readonly onMessage: (message: StreamMessage) => void;
  readonly onComment: (comment: string) => void;
}

export function createSseParser(handlers: SseHandlers): { feed(chunk: string): void; end(): void } {
  let buffer = "";
  let data: string[] = [];
  let type = "message";
  let id: string | null = null;
  const dispatch = () => {
    if (data.length > 0) handlers.onMessage({ id, type, data: data.join("\n") });
    data = [];
    type = "message";
  };
  const line = (raw: string) => {
    if (raw.length === 0) {
      dispatch();
      return;
    }
    if (raw.startsWith(":")) {
      handlers.onComment(raw.slice(1).trim());
      return;
    }
    const colon = raw.indexOf(":");
    const field = colon < 0 ? raw : raw.slice(0, colon);
    let value = colon < 0 ? "" : raw.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    switch (field) {
      case "event":
        type = value;
        break;
      case "data":
        data.push(value);
        break;
      case "id":
        if (!value.includes("\0")) id = value;
        break;
      default:
        break;
    }
  };
  return {
    feed(chunk) {
      buffer += chunk;
      let index: number;
      while ((index = buffer.search(/\r\n|\r|\n/)) >= 0) {
        const raw = buffer.slice(0, index);
        buffer = buffer.slice(index + (buffer.startsWith("\r\n", index) ? 2 : 1));
        line(raw);
      }
    },
    end() {
      if (buffer.length > 0) line(buffer);
      buffer = "";
      dispatch();
    },
  };
}

/** Read a `ReadableStream` of bytes through the parser until it ends or the signal aborts. */
export async function readSseStream(body: ReadableStream<Uint8Array>, handlers: SseHandlers, signal: AbortSignal): Promise<void> {
  const parser = createSseParser(handlers);
  const decoder = new TextDecoder();
  const reader = body.getReader();
  const abort = () => {
    void reader.cancel();
  };
  signal.addEventListener("abort", abort, { once: true });
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      parser.feed(decoder.decode(value, { stream: true }));
    }
    parser.end();
  } finally {
    signal.removeEventListener("abort", abort);
  }
}
